import { useEffect, useRef, useState } from 'react';
import {
  ConversationQueryPagination,
  ConversationThread,
  dispatch,
  getQueryData,
  getSelectedTimeRange,
  getWorkspaceId,
  updateQueryThread,
  useAppSelector,
  useLazyGetConversationQueryDetailsQuery,
} from '@zonofi/zono-money-store';
import { isNil, uniqBy } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { SELLER_WEBAPP_ROUTES } from '../../constants';
import {
  getDateRangeFromTimeRange,
  getIsoWithMicroseconds,
} from '../../components';

export const useSemanticQueryDetails = () => {
  const lastItemRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const queryData = useAppSelector(getQueryData);
  const workspaceId = useAppSelector(getWorkspaceId);
  const selectedTimeRange = useAppSelector(getSelectedTimeRange);

  const [getConversationQueryDetails, conversationQueryDetailsResponse] =
    useLazyGetConversationQueryDetailsQuery();

  const [queryConversationThreads, setQueryConversationThreads] = useState<
    ConversationThread[]
  >([]);
  const [paginationData, setPaginationData] =
    useState<ConversationQueryPagination>();
  const [refresh, setRefresh] = useState(0);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const fetchThreads = (reset = false) => {
    if (!queryData?.queryType) return;

    const { startTime, endTime } = getDateRangeFromTimeRange(selectedTimeRange);

    const paginationParams =
      !reset && paginationData?.hasMore
        ? {
            lastEvaluatedConversationThreadId:
              paginationData.lastEvaluatedConversationThreadId,
            lastEvaluatedStartMessageTimetoken:
              paginationData.lastEvaluatedStartMessageTimetoken,
          }
        : {};

    if (!reset) setIsFetchingMore(true);

    getConversationQueryDetails({
      params: {
        sellerWorkspaceId: workspaceId,
        startTime: getIsoWithMicroseconds(startTime),
        endTime: getIsoWithMicroseconds(endTime),
        queryType: queryData.queryType,
        ...paginationParams,
      },
    });
  };

  useEffect(() => {
    setQueryConversationThreads([]);
    setPaginationData(undefined);
    setIsFetchingMore(false);
    fetchThreads(true);
  }, [refresh, queryData?.queryType, selectedTimeRange]);

  useEffect(() => {
    if (
      conversationQueryDetailsResponse.isSuccess &&
      conversationQueryDetailsResponse?.data
    ) {
      const { data = [], pagination } = conversationQueryDetailsResponse.data;
      setQueryConversationThreads((prev) =>
        uniqBy(
          isNil(paginationData) ? data : [...prev, ...data],
          'conversationId'
        )
      );
      setPaginationData(pagination);
      setIsFetchingMore(false);
    } else if (conversationQueryDetailsResponse?.error) {
      setIsFetchingMore(false);
    }
  }, [conversationQueryDetailsResponse, paginationData]);

  useEffect(() => {
    if (!lastItemRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && paginationData?.hasMore) {
          fetchThreads();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(lastItemRef.current);
    return () => observer.disconnect();
  }, [
    paginationData?.hasMore,
    paginationData?.lastEvaluatedConversationThreadId,
    paginationData?.lastEvaluatedStartMessageTimetoken,
  ]);

  const onRefresh = () => {
    setRefresh((prev) => prev + 1);
  };

  const onQueryThreadPress = (thread: ConversationThread) => {
    dispatch(
      updateQueryThread({
        ...thread,
        backgroundColor: queryData?.color,
        queryType: queryData?.queryType,
      })
    );
    navigate(
      SELLER_WEBAPP_ROUTES.SEMANTIC_QUERY_CONVERSATION.replace(
        ':conversationId',
        thread?.conversationId
      )
    );
  };

  return {
    queryData,
    queryConversationThreads,
    isLoading:
      isNil(paginationData) &&
      (conversationQueryDetailsResponse.isUninitialized ||
        conversationQueryDetailsResponse.isFetching),
    isFetchingMore:
      isFetchingMore && conversationQueryDetailsResponse.isFetching,
    onQueryThreadPress,
    onRefresh,
    lastItemRef,
    paginationData,
  };
};
